import {
  useDocumentUploads,
  postSelfieConsent,
  resetSelfie,
} from "@ca-dmv-radv/data";
import { useTranslation } from "@ca-dmv-radv/translation";
import {
  StatusContainer,
  STATUS_READY,
  STATUS_VERIFYING,
  STATUS_IN_PROGRESS,
  STATUS_ERROR,
  Button,
  BUTTON_STYLE_SECONDARY,
} from "@ca-dmv/core";
import { Modal } from "@ca-dmv/modal";
import React, { useState, useEffect, useRef } from "react";
import { showStatus } from "../../mdl/utils";
import ReviewPhotoTemplate from "../PresenceVerification/ReviewPhotoTemplate";
import "./styles.css";

const RULE_MESSAGES = {
  FACE_SIZE: {
    label: "Face is too small in the image.",
    hint: "Please move closer to the camera.",
  },
  EYES_AT_CAMERA: {
    label: "Eyes aren't looking at the camera.",
    hint: "Look directly into the camera lens.",
  },
  BRIGHTNESS: {
    label: "Photo brightness is out of range.",
    hint: "Ensure your face is well lit with no harsh shadows.",
  },
  NO_SHADOWS: {
    label: "Shadows detected on your face.",
    hint: "Ensure your face is well lit with no harsh shadows.",
  },
  SHARPNESS: {
    label: "Photo isn't sharp enough.",
    hint: "Hold the camera steady and make sure the image is in focus.",
  },
  FACE_DETECTED: {
    label: "No face detected.",
    hint: "Make sure your face is clearly visible in the frame.",
  },
  FACE_CENTER_Y: {
    label: "Face isn't centered.",
    hint: "Center your face vertically in the frame.",
  },
  NO_SUNGLASSES: {
    label: "Sunglasses detected.",
    hint: "Remove sunglasses before taking your photo.",
  },
  NO_HEADWEAR: {
    label: "Headwear is covering your face.",
    hint: "Remove hats or caps (religious headwear is allowed).",
  },
  EYES_OPEN: {
    label: "Eyes appear closed.",
    hint: "Keep your eyes open and look at the camera.",
  },
  NEUTRAL_EXPR: {
    label: "Expression isn't neutral.",
    hint: "Keep a neutral expression without smiling.",
  },
  NO_GLARE: {
    label: "Glare detected on glasses.",
    hint: "Adjust lighting or angle to reduce glare on your glasses.",
  },
  HEAD_YAW: {
    label: "Head is turned too far.",
    hint: "Face the camera directly, without turning your head.",
  },
  HEAD_PITCH: {
    label: "Head is tilted up or down too far.",
    hint: "Keep your head level, facing the camera.",
  },
  HEAD_ROLL: {
    label: "Head is tilted sideways too far.",
    hint: "Keep your head straight and level.",
  },
  DIM_WIDTH: {
    label: "Image resolution is too low.",
    hint: "Try retaking the photo with a higher-resolution camera.",
  },
  DIM_HEIGHT: {
    label: "Image resolution is too low.",
    hint: "Try retaking the photo with a higher-resolution camera.",
  },
};

const TRANSITION_HOLD_MS = 3000;

export default function SelfieIntegrityVerification() {
  const { documentUploads } = useDocumentUploads();
  const { t } = useTranslation();
  const [showReview, setShowReview] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevRpvStatusRef = useRef(null);

  const selfieDocument = documentUploads?.[15]?.[0];

  let status = showStatus(selfieDocument) || STATUS_READY;
  const rpvStatus = selfieDocument?.RPV?.status;
  if (["NYS", "INP"].includes(rpvStatus)) {
    status = STATUS_IN_PROGRESS;
  } else if (rpvStatus === "FAL") {
    status = STATUS_ERROR;
  }

  const getErrorList = (error) => {
    if (error == null) return [];
    let parsed = error;
    if (typeof error === "string") {
      try {
        parsed = JSON.parse(error);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(parsed?.failedRules)) return [];

    return parsed.failedRules
      .filter((rule) => rule.pass === false)
      .map((rule) => {
        const copy = RULE_MESSAGES[rule.ruleCode];
        return {
          key: rule.ruleCode ?? String(rule.id),
          label: copy ? copy.label : rule.description,
          hint: copy ? copy.hint : "",
        };
      });
  };

  const rpvErrors = rpvStatus === "FAL" ? getErrorList(rpvError) : [];
  const isVerifying = ["NYS", "INP"].includes(rpvStatus) || isTransitioning;

  if (isVerifying) {
    status = STATUS_VERIFYING;
  } else if (rpvStatus === "FAL") {
    status = STATUS_ERROR;
  }

  // Detect the transition from "still verifying" (NYS/INP) into a final
  // result (WUD/FAL). Hold the stepper at "step 2 complete, step 3 active"
  // for TRANSITION_HOLD_MS before revealing the final screen.
  useEffect(() => {
    const wasVerifying = ["NYS", "INP"].includes(prevRpvStatusRef.current);
    const isFinal = ["WUD", "FAL"].includes(rpvStatus);

    if (wasVerifying && isFinal) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_HOLD_MS);
      prevRpvStatusRef.current = rpvStatus;
      return () => clearTimeout(timer);
    }

    prevRpvStatusRef.current = rpvStatus;
  }, [rpvStatus]);

  const handleReviewSelfie = () => {
    setShowReview(true);
  };

  const handleRetakeSelfie = async () => {
    try {
      await resetSelfie();
    } catch (e) {
      console.error(
        "Failed to post rejection consent before retaking selfie",
        e
      );
    }
    window.location.href = "/rpc/presence-verification";
  };

  const step2Complete = isTransitioning;
  const step3Active = isTransitioning;

  return (
    <>
      <StatusContainer
        containerClass="mb-40 bp-md:mb-48 max-width--900"
        heading={
          <h3 className="mb-24 text--primary">
            {t(
              "screens-SelfieIntegrity-legend",
              "Selfie Integrity Verification"
            )}
          </h3>
        }
        headingClass="mb-24"
        status={isVerifying ? STATUS_VERIFYING : status}
      >
        <div>
          {!["NYS", "INP", "FAL"].includes(selfieDocument?.RPV?.status) &&
            !isTransitioning && (
              <>
                <p className="mb-16 text--blue-dark-2">
                  {t(
                    "screens-SelfieIntegrity-review-message",
                    "Please review your selfie before submitting your application."
                  )}
                </p>
                <Button
                  buttonClass="selfie-verify-button"
                  btnStyle={BUTTON_STYLE_SECONDARY}
                  label={t("screens-SelfieIntegrity-button", "Review Selfie")}
                  onClick={handleReviewSelfie}
                />
              </>
            )}
          {isVerifying && (
            <div className="selfie-verifying-card">
              <p className="selfie-verifying-subtext">
                {t(
                  "screens-SelfieIntegrity-verifying-subtext",
                  "Please wait while we verify your selfie."
                )}
              </p>

              <ol className="selfie-steps">
                <li className="selfie-step selfie-step--completed">
                  <span className="selfie-step-marker">
                    <span className="selfie-step-check" aria-hidden="true" />
                  </span>
                  <span className="selfie-step-label">
                    {t(
                      "screens-SelfieIntegrity-step1-label",
                      "Selfie submitted"
                    )}
                  </span>
                  <span className="selfie-step-status selfie-step-status--completed">
                    {t("screens-SelfieIntegrity-step1-status", "Completed")}
                  </span>
                </li>

                <li
                  className={
                    step2Complete
                      ? "selfie-step selfie-step--completed"
                      : "selfie-step selfie-step--active"
                  }
                >
                  <span className="selfie-step-marker">
                    {step2Complete ? (
                      <span className="selfie-step-check" aria-hidden="true" />
                    ) : (
                      "2"
                    )}
                  </span>
                  <span className="selfie-step-label">
                    {t(
                      "screens-SelfieIntegrity-step2-label",
                      "Checking image quality"
                    )}
                  </span>
                  <span
                    className={
                      step2Complete
                        ? "selfie-step-status selfie-step-status--completed"
                        : "selfie-step-status selfie-step-status--active"
                    }
                  >
                    {step2Complete
                      ? t(
                        "screens-SelfieIntegrity-step2-status-done",
                        "Completed"
                      )
                      : t("screens-SelfieIntegrity-step2-status", "Verifying")}
                  </span>
                </li>

                <li
                  className={
                    step3Active
                      ? "selfie-step selfie-step--active"
                      : "selfie-step selfie-step--pending"
                  }
                >
                  <span className="selfie-step-marker">3</span>
                  <span className="selfie-step-label">
                    {t(
                      "screens-SelfieIntegrity-step3-label",
                      "Preparing final results"
                    )}
                  </span>
                  <span
                    className={
                      step3Active
                        ? "selfie-step-status selfie-step-status--active"
                        : "selfie-step-status selfie-step-status--pending"
                    }
                  >
                    {step3Active
                      ? t(
                        "screens-SelfieIntegrity-step3-status-active",
                        "Verifying"
                      )
                      : t("screens-SelfieIntegrity-step3-status", "Pending")}
                  </span>
                </li>
              </ol>

              <div className="selfie-info-box">
                <span className="selfie-info-icon" aria-hidden="true" />
                <p className="selfie-info-text">
                  {t(
                    "screens-SelfieIntegrity-info-text",
                    "This usually takes less than 30 seconds. You'll be notified here once it's complete."
                  )}
                </p>
              </div>
            </div>
          )}

          {!isTransitioning && selfieDocument?.RPV?.status === "FAL" && (
            <>
              <div className="selfie-error-card">
                <div className="selfie-error-header">
                  <span className="selfie-error-icon" aria-hidden="true" />
                  <p className="selfie-error-title">
                    {t(
                      "screens-SelfieIntegrity-fal-title",
                      "Your selfie did not meet the requirements."
                    )}
                  </p>
                </div>

                <p className="selfie-error-subtext">
                  {t(
                    "screens-SelfieIntegrity-fal-subtext",
                    "Please fix the following before continuing:"
                  )}
                </p>

                {rpvErrors.length > 0 && (
                  <ul className="selfie-error-list">
                    {rpvErrors.map(({ key, label, hint }) => (
                      <li key={key}>
                        <strong>{label}</strong>{" "}
                        <span className="selfie-error-hint">{hint}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <Button
                buttonClass="selfie-retake-button"
                btnStyle={BUTTON_STYLE_SECONDARY}
                label={t(
                  "screens-SelfieIntegrity-retake-button",
                  "Retake Selfie"
                )}
                onClick={handleRetakeSelfie}
              />
            </>
          )}
        </div>
      </StatusContainer>

      {showReview && (
        <Modal
          modalTitle={
            <div className="modalHeader">
              <span>
                {t(
                  "screens-SelfieIntegrity-review-title",
                  "Photo Confirmation"
                )}
              </span>

              <div
                className="closeButton"
                role="button"
                tabIndex={0}
                aria-label="Close"
                onClick={() => setShowReview(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowReview(false);
                  }
                }}
              >
                ✕
              </div>
            </div>
          }
          onClose={() => setShowReview(false)}
        >
          <ReviewPhotoTemplate
            image={null}
            approveLabel={t("screens-SelfieIntegrity-approve", "Approve")}
            retryLabel={t("screens-SelfieIntegrity-reject", "Reject")}
            subtitleLabel={t(
              "screens-SelfieIntegrity-subtitle",
              "Confirm Photo for Submission"
            )}
            instructionLabel="Select “Approve” to confirm, or “Reject” to retake your photo."
            onRetry={async () => {
              try {
                await postSelfieConsent(false);
                await handleRetakeSelfie();
              } catch (e) {
                console.error("Failed to post rejection consent", e);
              }
              setShowReview(false);
            }}
            onApprove={async () => {
              try {
                await postSelfieConsent(true);
              } catch (e) {
                console.error("Failed to post approval consent", e);
              }
              setShowReview(false);
            }}
          />
        </Modal>
      )}
    </>
  );
}
